<template>
  <header
    v-if="!isLoginRoute"
    class="app-header"
    :class="{ expanded: sidebarExpanded }"
  >
    <!-- Brand -->
    <div class="header-brand">
      <span class="brand-pos">POS</span><span class="brand-wave">WAVE</span>
    </div>

    <!-- Page title -->
    <div class="header-center">
      <span class="page-title">{{ currentPageTitle }}</span>
    </div>

    <!-- User pill + dropdown -->
    <div class="header-right" ref="dropdownRef">
      <button
        class="user-pill"
        :class="{ active: dropdownOpen }"
        @click="toggleDropdown"
        :aria-expanded="dropdownOpen"
      >
        <div class="user-avatar" aria-hidden="true">{{ initials }}</div>
        <div class="user-info">
          <span class="user-name">{{ loading ? 'Cargando…' : `${username} ${apellido}` }}</span>
          <span class="user-role">{{ loading ? '' : rol }}</span>
        </div>
        <i class="bi bi-chevron-down user-caret" :class="{ rotated: dropdownOpen }" aria-hidden="true"></i>
      </button>

      <transition name="dd-fade">
        <div v-if="dropdownOpen" class="user-dropdown" role="menu">
          <!-- User summary -->
          <div class="dd-header">
            <div class="dd-avatar-lg">{{ initials }}</div>
            <div class="dd-user-info">
              <span class="dd-name">{{ username }} {{ apellido }}</span>
              <span class="dd-role">{{ rol }}</span>
            </div>
          </div>
          <div class="dd-divider"></div>

          <!-- Mi cuenta -->
          <router-link to="/config-page" class="dd-item" @click="closeDropdown" role="menuitem">
            <div class="dd-item-icon"><i class="bi bi-person-fill"></i></div>
            <div class="dd-item-text">
              <span class="dd-item-label">Mi cuenta</span>
              <span class="dd-item-sub">Perfil y configuración personal</span>
            </div>
          </router-link>

          <!-- Documentación -->
          <router-link to="/documentacion" class="dd-item" @click="closeDropdown" role="menuitem">
            <div class="dd-item-icon"><i class="bi bi-book-fill"></i></div>
            <div class="dd-item-text">
              <span class="dd-item-label">Documentación</span>
              <span class="dd-item-sub">Guías y tutoriales del sistema</span>
            </div>
          </router-link>

          <div class="dd-divider"></div>

          <!-- Cerrar sesión -->
          <button class="dd-item danger" @click="handleLogout" role="menuitem">
            <div class="dd-item-icon"><i class="bi bi-box-arrow-right"></i></div>
            <div class="dd-item-text">
              <span class="dd-item-label">Cerrar sesión</span>
            </div>
          </button>
        </div>
      </transition>
    </div>

    <!-- Logout modal -->
    <LogoutConfirmModal
      :isVisible="showLogoutModal"
      @confirm="confirmLogout"
      @cancel="cancelLogout"
    />
  </header>
</template>

<script>
import axios from 'axios';
import LogoutConfirmModal from '@/components/LogoutConfirmModal.vue';
const { getApi } = require('../../config/getApiUrl.js');

const ROUTE_TITLES = {
  '/home':                 'Dashboard',
  '/ventas':               'Ventas',
  '/administrar-ventas':   'Administrar Ventas',
  '/administrar-compras':  'Administrar Compras',
  '/compras':              'Crear Compra',
  '/cotizaciones':         'Cotizaciones',
  '/clientes':             'Clientes',
  '/proveedores':          'Proveedores',
  '/productos':            'Productos',
  '/categorias':           'Categorías',
  '/cuentas-por-cobrar':   'Cuentas por Cobrar',
  '/cuentas-por-pagar':    'Cuentas por Pagar',
  '/arqueo-caja':          'Arqueo de Caja',
  '/devoluciones':         'Devoluciones',
  '/reporte':              'Reportes',
  '/libros-fiscales':      'Libros Fiscales',
  '/nomina':               'Planilla',
  '/admin-invenario':      'Alertas',
  '/empleados':            'Usuarios',
  '/sucursales':           'Sucursales',
  '/config-page':          'Configuración',
  '/documentacion':        'Documentación',
};

export default {
  name: 'AppHeader',
  components: { LogoutConfirmModal },
  props: {
    isDarkMode:      { type: Boolean, default: false },
    sidebarExpanded: { type: Boolean, default: false },
    isLoginRoute:    { type: Boolean, default: false },
  },
  emits: ['logout'],
  data() {
    return {
      username:         '',
      apellido:         '',
      rol:              '',
      loading:          true,
      dropdownOpen:     false,
      showLogoutModal:  false,
    };
  },
  computed: {
    initials() {
      return ((this.username?.[0] || '') + (this.apellido?.[0] || '')).toUpperCase() || '?';
    },
    currentPageTitle() {
      return ROUTE_TITLES[this.$route?.path] || '';
    },
  },
  methods: {
    async fetchProfile() {
      try {
        const token = localStorage.getItem('auth');
        if (!token) return;
        const res = await axios.get(`${getApi()}/perfil`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.username = res.data.nombre   || '';
        this.apellido = res.data.apellido || '';
        this.rol      = res.data.cargo    || '';
      } catch { /* usuario no logueado aún */ }
      finally { this.loading = false; }
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    handleLogout() {
      this.dropdownOpen = false;
      this.showLogoutModal = true;
    },
    confirmLogout() {
      this.showLogoutModal = false;
      this.$emit('logout');
    },
    cancelLogout() {
      this.showLogoutModal = false;
    },
    onClickOutside(e) {
      if (this.$refs.dropdownRef && !this.$refs.dropdownRef.contains(e.target)) {
        this.dropdownOpen = false;
      }
    },
    onEsc(e) {
      if (e.key === 'Escape') this.dropdownOpen = false;
    },
  },
  mounted() {
    this.fetchProfile();
    document.addEventListener('mousedown', this.onClickOutside);
    document.addEventListener('keydown', this.onEsc);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onClickOutside);
    document.removeEventListener('keydown', this.onEsc);
  },
  watch: {
    isLoginRoute(val) {
      if (!val) this.fetchProfile();
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
* { font-family: 'Montserrat', sans-serif; box-sizing: border-box; }

/* ── Header bar ── */
.app-header {
  position: fixed;
  top: 0; left: 80px; right: 0;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 20px;
  background: #141414;
  border-bottom: 1px solid rgba(192,157,98,.18);
  box-shadow: 0 2px 12px rgba(0,0,0,.35);
  z-index: 900;
  transition: left .3s cubic-bezier(.4,0,.2,1);
}
.app-header.expanded { left: 210px; }

/* ── Brand ── */
.header-brand { display: flex; align-items: baseline; gap: 1px; flex-shrink: 0; }
.brand-pos  { font-size: 17px; font-weight: 800; color: #c09d62; letter-spacing: .04em; }
.brand-wave { font-size: 17px; font-weight: 400; color: rgba(192,157,98,.5); letter-spacing: .04em; }

/* ── Center ── */
.header-center { flex: 1; }
.page-title { font-size: 12px; font-weight: 500; color: rgba(214,208,200,.4); letter-spacing: .02em; }

/* ── Right ── */
.header-right { position: relative; flex-shrink: 0; }

/* ── User pill ── */
.user-pill {
  display: flex; align-items: center; gap: 9px;
  padding: 5px 10px 5px 6px;
  border-radius: 10px;
  border: 1px solid rgba(192,157,98,.2);
  background: rgba(192,157,98,.06);
  cursor: pointer;
  transition: background .18s, border-color .18s;
}
.user-pill:hover, .user-pill.active {
  background: rgba(192,157,98,.13);
  border-color: rgba(192,157,98,.4);
}
.user-avatar {
  width: 30px; height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, #c09d62 0%, #8c7040 100%);
  color: #111;
  font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.user-info { display: flex; flex-direction: column; min-width: 0; }
.user-name {
  font-size: 12px; font-weight: 600; color: #c09d62;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px; line-height: 1.2;
}
.user-role { font-size: 10px; color: rgba(214,208,200,.45); line-height: 1.2; white-space: nowrap; }
.user-caret {
  font-size: 9px; color: rgba(192,157,98,.5);
  transition: transform .2s;
}
.user-caret.rotated { transform: rotate(180deg); }

/* ── Dropdown ── */
.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 230px;
  background: #1a1a1a;
  border: 1px solid rgba(192,157,98,.22);
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,.55);
  overflow: hidden;
  z-index: 1100;
}

/* Dropdown header (user summary) */
.dd-header {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 14px 12px;
}
.dd-avatar-lg {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #c09d62 0%, #8c7040 100%);
  color: #111; font-size: 13px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}
.dd-user-info { min-width: 0; }
.dd-name {
  display: block; font-size: 12px; font-weight: 700; color: #c09d62;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dd-role { font-size: 10px; color: rgba(214,208,200,.45); }

.dd-divider { height: 1px; background: rgba(192,157,98,.15); margin: 0 10px; }

/* Dropdown items */
.dd-item {
  display: flex; align-items: center; gap: 11px;
  width: 100%; padding: 11px 14px;
  background: transparent; border: none;
  text-decoration: none; cursor: pointer;
  transition: background .15s;
}
.dd-item:hover { background: rgba(192,157,98,.08); }
.dd-item.danger:hover { background: rgba(220,60,60,.08); }

.dd-item-icon {
  width: 30px; height: 30px;
  border-radius: 8px; flex-shrink: 0;
  background: rgba(192,157,98,.1);
  border: 1px solid rgba(192,157,98,.15);
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; color: #c09d62;
  transition: background .15s;
}
.dd-item.danger .dd-item-icon {
  background: rgba(220,60,60,.1);
  border-color: rgba(220,60,60,.2);
  color: #e05555;
}

.dd-item-text { display: flex; flex-direction: column; min-width: 0; text-align: left; }
.dd-item-label { font-size: 12px; font-weight: 600; color: #d6d0c8; white-space: nowrap; }
.dd-item-sub   { font-size: 10px; color: rgba(214,208,200,.4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dd-item.danger .dd-item-label { color: #e05555; }

/* Transition */
.dd-fade-enter-active, .dd-fade-leave-active { transition: opacity .15s ease, transform .15s ease; }
.dd-fade-enter-from, .dd-fade-leave-to { opacity: 0; transform: translateY(-6px); }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .app-header { left: 70px; }
  .app-header.expanded { left: 190px; }
}
@media (max-width: 768px) {
  .app-header { left: 0; }
  .app-header.expanded { left: 0; }
  .header-brand { display: none; }
  .user-name { max-width: 90px; }
}
@media (max-width: 480px) {
  .user-info { display: none; }
  .user-caret { display: none; }
  .user-pill { padding: 4px; }
  .user-dropdown { right: -8px; }
}
</style>
